import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

type DesktopCommentSheetProps = {
  open: boolean;
  onClose: () => void;
  children?: React.ReactNode;
};

const TRANSITION_DURATION = 280; // ms

// Overlay root: fills the sandbox content area
const OverlayRoot = styled(Box)<{ isVisible: boolean }>(({ isVisible }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'center',
  pointerEvents: isVisible ? 'auto' : 'none',
  '@media (prefers-reduced-motion: reduce)': {
    '& *': {
      transition: 'none !important',
    },
  },
}));

// Backdrop: dimmed area covering the entire overlay, clickable to close
const Backdrop = styled(Box)<{ isVisible: boolean }>(({ isVisible }) => ({
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  opacity: isVisible ? 1 : 0,
  transition: `opacity ${TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
  cursor: 'pointer',
}));

// Sheet layer: contains close button and sheet, handles slide animation
// pointer-events: none so clicks pass through to backdrop
// maxHeight: calc(100% - 80px) ensures 80px breathing room at top of container
const SheetLayer = styled(Box)<{ isVisible: boolean }>(({ isVisible }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  maxHeight: 'calc(100% - 80px)',
  transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
  transition: `transform ${TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
  pointerEvents: 'none', // Let clicks pass through to backdrop
}));

// Floating close button with glass effect
const FloatingCloseButton = styled('button')({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  border: '1px solid rgba(255, 255, 255, 0.28)',
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginBottom: '12px',
  pointerEvents: 'auto', // Re-enable pointer events for button
  // Glass effect
  background: 'rgba(0, 0, 0, 0.24)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  boxShadow: `
    0 4px 9px rgba(0, 0, 0, 0.16),
    inset 0 2px 24px rgba(0, 0, 0, 0.20),
    inset 0 -2px 24px rgba(255, 255, 255, 0.20)
  `,
  '&:hover': {
    background: 'rgba(0, 0, 0, 0.32)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
});

// Sheet container: white bottom sheet with rounded top corners
const SheetContainer = styled(Box)({
  backgroundColor: 'var(--dk-bg, #FFFFFF)',
  borderRadius: '20px 20px 0 0',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
  flex: 1,
  minHeight: 0,
  pointerEvents: 'auto', // Re-enable pointer events for sheet content
});

// Content area (scrollable)
const SheetContent = styled(Box)({
  flex: 1,
  overflow: 'auto',
  padding: '16px 24px 24px',
});

const DesktopCommentSheet: React.FC<DesktopCommentSheetProps> = ({
  open,
  onClose,
  children,
}) => {
  // shouldRender: controls whether component is in DOM
  // isVisible: controls animation state (visible = open position)
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open) {
      // Opening: mount first, then animate in
      setShouldRender(true);
      // Use requestAnimationFrame to ensure DOM is ready before animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      // Closing: animate out first, then unmount
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, TRANSITION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!shouldRender) return null;

  return (
    <OverlayRoot isVisible={isVisible}>
      <Backdrop isVisible={isVisible} onClick={onClose} />
      <SheetLayer isVisible={isVisible}>
        <FloatingCloseButton onClick={onClose} aria-label="닫기">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 6L18 18"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <path
              d="M18 6L6 18"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </FloatingCloseButton>
        <SheetContainer>
          <SheetContent>
            {children || (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '200px',
                  color: 'var(--dk-text-strong, #666)',
                  fontSize: '16px',
                }}
              >
                댓글 영역
              </Box>
            )}
          </SheetContent>
        </SheetContainer>
      </SheetLayer>
    </OverlayRoot>
  );
};

export default DesktopCommentSheet;
